export const meta = {
  id: '28-demonstratives-2',
  order: 28,
  checkFocus: { numbers: ['col'] },   // what the end-of-lesson check tests first
  titleFi: 'Joku, jokin, kaikki',
  titleEn: 'Demonstratives 2',
  duolingoUnit: 'duo-33-demonstratives-2',
  // + Sanapaja's own words this lesson first needs (docs/COURSE.md "Own words in the lessons"; waves/wave-NN/lessons.json)
  lemmaIds: ['olvȳn', 'mirror', 'ours:epviapos', 'ours:bariapos', 'ours:arritir', 'ours:ruarilaksālion', 'ours:pystir', 'ours:qrimmajēptys', 'ours:rhēdessiarzir', 'ours:pirtȳdrakio', 'ours:qrimpālenon', 'ours:zderēptir', 'ours:brōzoqitta', 'ours:gūrēntōñe', 'ours:ruarilaksenka', 'ours:grammatika', 'ours:teknologia', 'ours:media', 'ours:data', 'ours:gravitationi', 'ours:sellula', 'ours:satelliti'],
  exampleIds: [
    'ex-9038d5206b40',
    'ex-347c9dfd25d7',
    'got-s3e04-013',
    'got-s3e04-009',
    'ex-c7292c764a88',
    'ex-d5afeb2c0e99',
    'ex-afb4e01ff3d4',
    'ex-10026cb8c937',
    'got-s7e04-001',
    'ex-c541cd3b6695',
  ],
  sources: [
    'https://wiki.languageinvention.com/index.php?title=Appendix:High_Valyrian_Duolingo_Tips_and_Notes#Demonstratives_2',
    'https://wiki.languageinvention.com/index.php?title=User:Najahho/High_Valyrian_Pronouns#Indefinite_pronouns',
    'https://wiki.languageinvention.com/index.php?title=High_Valyrian_Grammar#Number',
    'https://wiki.languageinvention.com/index.php?title=High_Valyrian_Grammar#Noun_Phrases',
    'https://wiki.languageinvention.com/index.php?title=High_Valyrian_Reference_Grammar#Sixth_Declension',
    'https://wiki.languageinvention.com/index.php?title=valar',
    'https://wiki.languageinvention.com/index.php?title=olvȳn',
    'https://wiki.languageinvention.com/index.php?title=mirror',
    'https://dothraki.com/2013/08/valyrian-numerals/',
  ],
  // the week (docs/COURSE.md "Lesson hub")
  grammar: ['numbers', 'indefinites'],
  grammarAlso: ['demonstratives'],
  drills: { numbers: ['col', 'colpl'] },
  showLines: ['got-s3e04-013', 'got-s3e04-009', 'got-s7e04-001'],
  pronunciation: { focus: 'gh', words: ['qrīdrughagon', 'ōghar', 'ñōghybon', 'ñōghe'], lines: ['got-s3e04-013', 'got-s3e04-009', 'got-s7e04-001'] },
  letter: {
    fi: 'Kirjoita jostakin, jota kukaan ei tiedä, ja jostakin, jonka kaikki tietävät.',
    en: 'Write about something nobody knows and something everybody knows.',
  },
  // an everyday scene of ours (approved 2026-10-07, together/everyday/): the week has no show scene of its own
  scene: 'ours-kotiin',
}

# Joku, jokin, kaikki

Tähän asti olet osoittanut asioita sormella: *tämä*, *tuo*. Tällä oppitunnilla puhutaan määrästä ja epävarmuudesta: *joku*, *ei kukaan*, *muut*, *monet*, *kaikki*. Samalla tutustut kahteen lukuun, joita suomessa ei ole lainkaan: **muutamaan** ja **kollektiiviin**. Ne ovat korkeavalyrian omintakeisin piirre, joten edetään rauhassa.

## Viisi juurta

Korkeavalyrian epämääräiset sanat rakentuvat viidestä juuresta. Kun tunnet juuren, tunnistat koko sanaperheen:

- <Hv>mir-</Hv> – jokin, joku, mikä tahansa
- <Hv>dōr-</Hv> – ei mikään, ei kukaan
- <Hv>tol-</Hv> – muu, toinen
- <Hv>olv-</Hv> – moni, paljon
- <Hv>tolv-</Hv> – kaikki, jokainen

Juureen liitetään pääte, ja pääte kertoo, onko sana **adjektiivi** (se seisoo substantiivin vieressä ja kuvailee sitä) vai **pronomini** (se seisoo yksinään substantiivin paikalla). Tähän kannattaa kiinnittää huomiota, sillä päätteet menevät välillä päällekkäin: sama loppu voi olla yhdessä sanassa adjektiivin taivutusmuoto ja toisessa pronominin perusmuoto.

Adjektiivit ovat <Hv glossed>mirre</Hv> ’jokin, mikä tahansa’, <Hv glossed>dōre</Hv> ’ei yksikään’, <Hv glossed>tolie</Hv> ’muu’, <Hv glossed>olvie</Hv> ’moni’ ja <Hv glossed>tolvie</Hv> ’jokainen’. Ne taipuvat kuten muutkin adjektiivit eli mukautuvat substantiiviinsa.

Pronomineissa on tuttu jako, ja tässä suomi auttaa poikkeuksellisen paljon. Suomi erottaa **joku** (ihminen) ja **jokin** (asia), samoin **kukaan** ja **mikään**. Korkeavalyria vetää rajan elollisen ja elottoman välille täsmälleen samoin:

- <Hv glossed>mirtys</Hv> ’joku’ ~ <Hv glossed>mirros</Hv> ’jokin’
- <Hv glossed>daorys</Hv> ’ei kukaan’ ~ <Hv glossed>daorun</Hv> ’ei mikään’
- <Hv glossed>tolys</Hv> ’joku muu’ ~ <Hv glossed>tolion</Hv> ’jokin muu’
- <Hv glossed>tolvys</Hv> ’jokainen, kaikki (ihmiset)’ ~ <Hv glossed>tolvȳn</Hv> ’kaikki (asiat)’

Huomaa viimeinen pari: suomen *kaikki* ei kerro, puhutaanko ihmisistä vai tavaroista, mutta valyria kertoo sen aina. Elollisten pronominien loppu <Hv>-ys</Hv> on tuttu toisesta <Term id="deklinaatio">taivutusluokasta</Term> (esimerkiksi <Hv>dārys</Hv>). Huomaa myös, että ’ei kukaan’ ja ’ei mikään’ alkavat <Hv>daor-</Hv>, aivan kuten kieltosana <Hv>daor</Hv>, vaikka adjektiivi on <Hv>dōre</Hv>.

<Example id="ex-9038d5206b40" />

<Example id="ex-347c9dfd25d7" />

Daenerys käyttää kahta näistä samassa lupauksessa: kuka tahansa saa lähteä, eikä kukaan satuta häntä. <Hv>Mirre</Hv> on tässä adjektiivi (<Hv glossed>vala mirre</Hv> ’kuka tahansa mies’), <Hv>daorys</Hv> pronomini.

<Line id="got-s3e04-013" />

Astaporin portilla hän antaa käskyn, jossa adjektiivit seisovat samoin substantiivin **jäljessä**: <Hv glossed>vale tolvie</Hv> ’jokainen mies’ ja <Hv glossed>riñe dōre</Hv> ’ei yhtäkään lasta’. Petersonin mukaan <Term id="maarite">määräävät sanat</Term>, kuten *tolvie* ’jokainen’, ovat yleensä edellä, ja perässä ne kuulostavat ”viralliselta” – ja juuri sellainen hetki tämä on.

<Line id="got-s3e04-009" />

## Neljä lukua: tarkka ja epätarkka

Sitten siihen, mitä suomessa ei ole. Korkeavalyrian substantiivilla on **neljä lukua**: yksikkö, monikko, muutama ja kollektiivi. Ne on helpointa ymmärtää, kun kysyy kaksi kysymystä yhtä aikaa: *yksi vai useampi?* ja *tarkka vai epätarkka?*

- **Yksikkö** – tarkka yksikkö: täsmälleen yksi.
- **Kollektiivi** – tarkka monikko: kaikki, koko joukko.
- **Muutama** – epätarkka yksikkö: pieni, tarkemmin rajaamaton joukko.
- **Monikko** – epätarkka monikko: monta, ehkä kaksi, ehkä kaksikymmentä.

Peterson kertoo wikin <Hv l="vala" glossed>Valar</Hv>-sivulla, että koko järjestelmä syntyi George R. R. Martinin kahdesta lauseesta, <Hv>valar morghūlis</Hv> ja <Hv>valar dohaeris</Hv>. Koska <Hv glossed>valar</Hv> tarkoitti ’kaikkia miehiä’, se suorastaan vaati hänen mukaansa kollektiivia. Siitä hän päätyi jakamaan luvun kahteen ulottuvuuteen, määrään ja tarkkuuteen, ja neljäs ruutu, epätarkka yksikkö, sai nimen muutama.

<Note>
Suomi ei auta tässä. Partitiivi (*leipää*, *ihmisiä*) voi tuntua sukulaiselta, koska sekin ilmaisee rajaamatonta määrää, mutta se on sija, ei luku. Valyrian muutama ja kollektiivi ovat omia lukujaan, ja niillä on omat muotonsa kaikissa kahdeksassa sijassa. Älä siis yritä kääntää niitä mielessäsi partitiiviksi, vaan opettele ne uutena asiana.
</Note>

### Tunnistaminen: -n ja -r

Muutaman tunnistaa perusmuodon lopun **-n**:stä, kollektiivin **-r**:stä. Tämän oppitunnin sanat ovat juuri tällaisia: <Hv glossed>olvȳn</Hv> ’moni, monet, paljon’ on muutamassa, samoin <Hv l="tolvȳn">tolvȳn</Hv>, <Hv>daorun</Hv> ja <Hv glossed>mirrun</Hv> ’jotkut’. <Hv glossed>Mirror</Hv> ’mikä tahansa, mitä ikinä’ on kollektiivissa.

<Pronounce word="olvȳn" />

<Pronounce l="mirror" word="mirror" />

<Note>
**Kollektiivin nyrkkisääntö** Damien Halmelta (Academy of High Valyrian): vokaaliin päättyvä sana saa *r*:n (<Hv>kēli</Hv> → <Hv>kēlir</Hv>, <Hv>guēse</Hv> → <Hv>guēser</Hv>), konsonanttiin päättyvän sanan viimeinen konsonantti vaihtuu *r*:ksi (<Hv>gryves</Hv> → <Hv>gryver</Hv>, <Hv>genes</Hv> → <Hv>gener</Hv>). Kollektiivi pitää alkuperäisen sanan suvun.

Kollektiivi muistuttaa suomen joukkosanoja *lauma*, *parvi* ja *väki*: <Hv glossed>zokla</Hv> ’susi’ → <Hv>zoklar</Hv>, sudet yhtenä joukkona kuin susilauma; <Hv glossed>hontes</Hv> ’lintu’ → <Hv>honter</Hv>, linnut yhtenä parvena.
</Note>

### Verbin luku menee toisin päin kuin suomessa

Muutama ja kollektiivi ovat vain substantiivien lukuja. Verbeillä on pelkkä yksikkö ja monikko, joten niiden on valittava:

- **muutama** saa verbin **monikkoon**,
- **kollektiivi** saa verbin **yksikköön**.

Tässä suomalaisen korva johtaa helposti harhaan. Suomessa sanotaan *moni on iloinen*, yksikössä. Valyriassa ’moni’ on muutamassa, ja verbi on monikossa:

<Example id="ex-c7292c764a88" />

<Interlinear hv="Olv|ȳn kirin|i issi." gloss="moni-NOM.PC iloinen-NOM.PL olla-PRS.3PL" fi="Monet ovat iloisia." />

Kollektiivissa suomi taas auttaa: sanomme *väki on*, *kansa tulee*, vaikka puhumme monista ihmisistä. Valyrian kollektiivi toimii samoin, ja verbi on yksikössä. Tämä tulee vastaan monta kertaa seuraavilla oppitunneilla (*talvi*, *sää*, *vaatteet*).

<Note>
Kaksi virallista lausetta näyttää poikkeavan muutaman säännöstä. Daenerysin lohdutuksessa <Hv glossed>tolvȳn</Hv> ’kaikki’ saa yksikössä olevan verbin <Hv glossed>kessa</Hv> ’tulee olemaan’, ja lauseessa ”Jotkut syntyvät loputtomaan yöhön” <Hv l="mirrun">mirrun</Hv> saa yksikössä olevan passiivimuodon <Hv>sikiks</Hv>.

<Line id="got-s7e04-001" />

<Example id="ex-10026cb8c937" />

Petersonin mukaan omaksi sanakseen vakiintunut muutama saa yksikön verbin; *tolvȳn* ja *mirrun* ovat sanakirjassa omia sanojaan, mikä voisi selittää muodot (oma päätelmämme). Lähteet eroavat: wikin toisessa esimerkissä *tolvȳn* saa monikon verbin:

<Example id="ex-c541cd3b6695" />
</Note>

## Muutaman ja kollektiivin taivutus

Muutamalla ja kollektiivilla on omat sijapäätteensä. Ne muistuttavat lainasanojen taivutusta (siitä lisää seuraavalla oppitunnilla): esimerkiksi akkusatiivi päättyy **-i**:hin. Tässä <Hv>olvȳn</Hv>:

<Paradigm lemma="olvȳn" numbers="pc" />

Tunnusmerkit näkyvät selvästi. Perusmuodon <Hv>-n</Hv> säilyy useimmissa sijoissa, datiivissa sen perään tulee <Hv>-to</Hv>, ja lokatiivissa se kahdentuu:

<Compare fi="mone|lle" hv="olv|ȳnto" label="allatiivi ~ datiivi" />

<Compare fi="mone|n" hv="olv|ȳno" label="genetiivi ~ genetiivi" />

Instrumentaalissa ja komitatiivissa *n* väistyy: <Hv>olvȳsso</Hv>, <Hv>olvȳmmo</Hv>. Vokatiivi on sama kuin instrumentaali.

Ja tässä kollektiivi <Hv l="mirror">mirror</Hv>. Kaava on sama, mutta *n*:n paikalla on *r*:

<Paradigm lemma="mirror" numbers="col" />

Muodot vaihtelevat <Term id="deklinaatio">taivutusluokan</Term> teemavokaalin mukaan. Kuusukuisen <Hv>-a</Hv>-sanan kollektiivin datiivi päättyy <Hv>-ta</Hv>:han (<Hv glossed>sōnarta</Hv> ’talvelle’), kun taas sanan <Hv l="mirror">mirror</Hv> datiivi on <Hv l="mirror">mirrorto</Hv>. Tämän oppitunnin sanoissa tarvitset kuitenkin enimmäkseen nominatiivia, akkusatiivia ja genetiiviä, ja ne ovat helppoja: <Hv>-r</Hv>, <Hv>-ri</Hv>, <Hv>-ro</Hv> ja <Hv>-n</Hv>, <Hv>-ni</Hv>, <Hv>-no</Hv>.

<Example id="ex-d5afeb2c0e99" />

<Example id="ex-afb4e01ff3d4" />

<Interlinear hv="Ynot mirr|ori tep|ās!" gloss="minä-DAT mikä.tahansa-ACC.COL antaa-IMP.2SG" fi="Anna minulle mitä tahansa!" />

## Harjoituslauseita

Nämä on rakennettu tämän oppitunnin säännöillä. Katso erityisesti, missä luvussa verbi on.

<Practice hv="Olvȳn ipradis." fi="Monet syövät." />

<Practice hv="Mirrori ipradan." fi="Syön mitä tahansa." />

<Practice hv="Mirtys ēdrus." fi="Joku nukkuu." />

## Muistettavaksi

- Viisi juurta: <Hv>mir-</Hv>, <Hv>dōr-</Hv>, <Hv>tol-</Hv>, <Hv>olv-</Hv>, <Hv>tolv-</Hv>.
- Joku ~ jokin: elollisella pääte <Hv>-ys</Hv>, elottomalla oma päätteensä – kuten suomessa.
- Muutama päättyy -n:ään ja saa verbin monikkoon; kollektiivi päättyy -r:ään ja saa verbin yksikköön.
- Molemmilla on omat sijamuotonsa; akkusatiivi päättyy -i:hin.

<Vocab />

<Check />
